import {
  createPostCaptureSetupError,
  createSessionFlowPreflightError,
} from '@/lib/telemetry/sessionFlowFailures';
import { classifySessionFlowFailure } from '@heidi/runtime/performance';

export type CaptureStartFailureCode =
  | 'permission-denied'
  | 'device-in-use'
  | 'capture-initialization-failed'
  | 'unexpected-capture-error';

export type CaptureStartFailure =
  | { type: 'cancelled' }
  | { type: 'capture-failed'; code: CaptureStartFailureCode };

export type CaptureStopFailure = { code: 'capture-stop-rejected' };

export type RecordingStartOutcome =
  | { type: 'started' }
  | CaptureStartFailure
  | {
      type: 'setup-failed-capture-stopped';
      setupError: { code: 'timer-start-failed' };
      stopError?: CaptureStopFailure;
    }
  | {
      type: 'setup-failed-stop-unconfirmed';
      setupError: { code: 'timer-start-failed' };
      stopError: {
        code: 'capture-stop-unconfirmed';
        cause?: CaptureStopFailure['code'];
      };
    };

/** Catch boundary: discard the original value, including arbitrary text. */
export function normalizeCaptureStartFailure(error: unknown): CaptureStartFailure {
  try {
    const failure = classifySessionFlowFailure(error, 'recording_start');
    if (failure.outcome === 'cancelled') return { type: 'cancelled' };
    switch (failure.errorType) {
      case 'permission_denied':
        return { type: 'capture-failed', code: 'permission-denied' };
      case 'device_in_use':
        return { type: 'capture-failed', code: 'device-in-use' };
      case 'capture_initialization_failed':
        return { type: 'capture-failed', code: 'capture-initialization-failed' };
    }
  } catch {
    // Opaque objects may throw even when the classifier reads their fields.
  }
  return { type: 'capture-failed', code: 'unexpected-capture-error' };
}

/** Safe projection onto the existing session-flow telemetry vocabulary. */
export function recordingStartFailureForTelemetry(
  outcome: Exclude<RecordingStartOutcome, { type: 'started' }>
): Error {
  if (outcome.type === 'cancelled') {
    return createSessionFlowPreflightError(
      'StartRecordingCancelled',
      'Capture cancelled'
    );
  }
  if (outcome.type !== 'capture-failed') return createPostCaptureSetupError();
  switch (outcome.code) {
    case 'permission-denied':
      return createSessionFlowPreflightError('MicPermissionError', 'Capture denied');
    case 'device-in-use':
      return createSessionFlowPreflightError('DeviceInUseError', 'Capture unavailable');
    case 'capture-initialization-failed':
      return createSessionFlowPreflightError('StartRecordingFailed', 'Capture failed');
    case 'unexpected-capture-error':
      return new Error('Unexpected capture failure');
  }
}
